// Centralized configuration for custom reports
// All available base reports, their imports, and mappings are defined here

// Import all report components
import ActiveCustomerReport from "./AdministrationReports/ActiveCustomerReport";
import TasksReport from "./AdministrationReports/TasksReport";
import PlanRenewalReport from "./AdministrationReports/PlanRenewalReport";
import ClientTerminationReport from "./AdministrationReports/ClientTerminationReport";
import ClientStatusReport from "./AdministrationReports/ClientStatusReport";
import CustomerContactsReport from "./AdministrationReports/CustomerContactsReport";
import TimesheetsReport from "./TimesheetsReport";
import SCVisitReport from "./SupportCoordinationReports/SCVisitReport";
import EVVReport from "./EVVReports/EVVReport";

/**
 * List of all available base reports that can be used to create custom reports
 * Each report has:
 * - key: unique identifier for internal use
 * - name: display name (must match backend reportColumnDefinitions keys)
 * - component: the React component to render
 */
export const availableBaseReports = [
    { key: "activeCustomer", name: "Customer Data", component: ActiveCustomerReport },
    { key: "tasks", name: "Tasks Data", component: TasksReport },
    { key: "planRenewal", name: "Plan Data", component: PlanRenewalReport },
    { key: "customerContacts", name: "Contacts Data", component: CustomerContactsReport },
    { key: "timeManagement", name: "Timesheets Data", component: TimesheetsReport },
    { key: "scVisit", name: "Visits Data", component: SCVisitReport },
    { key: "evvReport", name: "EVV Data", component: EVVReport },
    //{ key: "clientTermination", name: "Client Termination Data", component: ClientTerminationReport },
    //{ key: "clientStatus", name: "Client Status Data", component: ClientStatusReport },
];

// Legacy name mapping for backwards compatibility with old saved reports
const legacyNameToKeyMap = {
    "Active Customer Report": "activeCustomer",
    "Tasks Report": "tasks",
    "Plan Renewal Report": "planRenewal",
    "Client Termination Report": "clientTermination",
    "Client Status Report": "clientStatus",
    "Customer Contacts Report": "customerContacts",
    "Time Management Report": "timeManagement",
    "SC Visit Report": "scVisit",
    "EVV Report": "evvReport"
};

/**
 * Get the report component by report key
 * @param {string} reportKey - The key of the report (e.g., "activeCustomer")
 * @returns {React.Component|null} The report component or null if not found
 */
export const getReportComponentByKey = (reportKey) => {
    const report = availableBaseReports.find(r => r.key === reportKey);
    return report ? report.component : null;
};

/**
 * Get the report component by report name (supports both new and legacy names)
 * @param {string} reportName - The name of the report (e.g., "Customer Data" or "Active Customer Report")
 * @returns {React.Component|null} The report component or null if not found
 */
export const getReportComponentByName = (reportName) => {
    // Try to find by current name first
    let report = availableBaseReports.find(r => r.name === reportName);
    
    // If not found, check if it's a legacy name and convert to key
    if (!report && legacyNameToKeyMap[reportName]) {
        const key = legacyNameToKeyMap[reportName];
        report = availableBaseReports.find(r => r.key === key);
    }
    
    // If still not found, try treating the reportName as a key
    if (!report) {
        report = availableBaseReports.find(r => r.key === reportName);
    }
    
    return report ? report.component : null;
};

/**
 * Get the report key from a report name (supports both new and legacy names)
 * @param {string} reportName - The name of the report (e.g., "Customer Data" or "Active Customer Report")
 * @returns {string|null} The report key or null if not found
 */
export const getReportKeyByName = (reportName) => {
    // Try to find by current name first
    let report = availableBaseReports.find(r => r.name === reportName);
    if (report) return report.key;
    
    // Check if it's a legacy name
    if (legacyNameToKeyMap[reportName]) {
        return legacyNameToKeyMap[reportName];
    }
    
    // Check if it's already a key
    report = availableBaseReports.find(r => r.key === reportName);
    if (report) return report.key;
    
    return null;
};

/**
 * Get the report display name from a key or name
 * @param {string} keyOrName - The key or name of the report
 * @returns {string} The display name or the original value if not found
 */
export const getReportDisplayName = (keyOrName) => {
    // Try to find by key first
    let report = availableBaseReports.find(r => r.key === keyOrName);
    if (report) return report.name;
    
    // Try to find by current name
    report = availableBaseReports.find(r => r.name === keyOrName);
    if (report) return report.name;
    
    // Check if it's a legacy name - return the legacy name as is
    if (legacyNameToKeyMap[keyOrName]) {
        const key = legacyNameToKeyMap[keyOrName];
        report = availableBaseReports.find(r => r.key === key);
        if (report) return report.name;
    }
    
    // Return the original value if not found
    return keyOrName;
};

/**
 * Get list of report names for dropdowns/selection
 * @returns {Array<string>} Array of report names
 */
export const getReportNames = () => {
    return availableBaseReports.map(r => r.name);
};
